import { useEffect, useState } from "react";

const LOGO_URL = "/smart-logo.jpeg";

export default function SplashScreen({ onDone }) {
    const [phase, setPhase] = useState("entering"); // entering -> showing -> leaving -> done

    useEffect(() => {
        const t1 = setTimeout(() => setPhase("showing"), 50);
        const t2 = setTimeout(() => setPhase("leaving"), 1900);
        const t3 = setTimeout(() => {
            setPhase("done");
            onDone?.();
        }, 2600);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
        };
    }, [onDone]);

    if (phase === "done") return null;

    return (
        <div
            className={`fixed inset-0 z-[9999] flex items-center justify-center bg-black transition-opacity duration-700 ease-out ${
                phase === "leaving" ? "opacity-0 pointer-events-none" : "opacity-100"
            }`}
            data-testid="splash-screen"
        >
            {/* Clock animation behind logo */}
            <div className="absolute" aria-hidden="true">
                <svg
                    width="520"
                    height="520"
                    viewBox="0 0 200 200"
                    className="opacity-30"
                    style={{
                        filter: "drop-shadow(0 0 20px rgba(212,175,55,0.4))",
                    }}
                >
                    <circle cx="100" cy="100" r="92" fill="none" stroke="#D4AF37" strokeWidth="0.8" />
                    <circle cx="100" cy="100" r="86" fill="none" stroke="#D4AF37" strokeWidth="0.4" opacity="0.5" />
                    {Array.from({ length: 12 }).map((_, i) => {
                        const a = (i * 30 - 90) * (Math.PI / 180);
                        const x1 = 100 + Math.cos(a) * 84;
                        const y1 = 100 + Math.sin(a) * 84;
                        const x2 = 100 + Math.cos(a) * 76;
                        const y2 = 100 + Math.sin(a) * 76;
                        return (
                            <line
                                key={`clock-tick-${i}`}
                                x1={x1}
                                y1={y1}
                                x2={x2}
                                y2={y2}
                                stroke="#D4AF37"
                                strokeWidth={i % 3 === 0 ? 2 : 1}
                            />
                        );
                    })}
                    {/* Hour hand */}
                    <line
                        x1="100"
                        y1="100"
                        x2="100"
                        y2="55"
                        stroke="#FCF6BA"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        style={{
                            transformOrigin: "100px 100px",
                            animation: "splashHour 2.4s linear forwards",
                        }}
                    />
                    {/* Minute hand */}
                    <line
                        x1="100"
                        y1="100"
                        x2="100"
                        y2="40"
                        stroke="#D4AF37"
                        strokeWidth="2"
                        strokeLinecap="round"
                        style={{
                            transformOrigin: "100px 100px",
                            animation: "splashMin 2.4s linear forwards",
                        }}
                    />
                    <circle cx="100" cy="100" r="3" fill="#D4AF37" />
                </svg>
            </div>

            {/* Logo zoom + fade */}
            <img
                src={LOGO_URL}
                alt="SMART DEMANDS 24/7 LIMITED"
                className="relative z-10 w-72 sm:w-96 h-auto"
                style={{
                    transform:
                        phase === "entering"
                            ? "scale(0.4)"
                            : phase === "showing"
                                ? "scale(1)"
                                : "scale(1.25)",
                    opacity: phase === "showing" ? 1 : phase === "entering" ? 0 : 0,
                    transition: "transform 1.6s cubic-bezier(.2,.8,.2,1), opacity 0.7s ease-out",
                    filter: "drop-shadow(0 0 28px rgba(212,175,55,0.45))",
                }}
                data-testid="splash-logo"
            />

            <div
                className="absolute bottom-16 left-0 right-0 text-center px-6"
                style={{
                    opacity: phase === "showing" ? 1 : 0,
                    transition: "opacity 0.7s ease-out 0.6s",
                }}
            >
                <div className="overline">Your demands · 24/7</div>
                <div className="font-serif italic text-2xl gold-grad mt-2">Anytime, Anywhere.</div>
                <div
                    className="mt-6 text-[10px] sm:text-[11px] tracking-[0.35em] uppercase text-amber-300/80"
                    data-testid="splash-llc-name"
                >
                    SMART DEMANDS 24/7 LIMITED
                </div>
                <div className="mt-1 text-[9px] tracking-[0.25em] uppercase text-muted-foreground/70">
                    A company duly incorporated under the Laws of Jamaica
                </div>
            </div>

            <style>{`
                @keyframes splashHour {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(180deg); }
                }
                @keyframes splashMin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(720deg); }
                }
            `}</style>
        </div>
    );
}
